import { z } from "zod"

/**
 * Three lanes of feedback intake.
 *
 * - `response_feedback` is thumbs up/down on a chat answer. Lightweight, never
 *   creates a support ticket.
 * - `bug` is a structured bug report. Only escalates to support when
 *   `shouldEscalate()` returns true for the user's answers.
 * - `incident` is a user-reported incident (service broken for multiple
 *   people). Always escalates.
 *
 * Keep this file 1:1 in sync with `app/backend/routes/feedback.py`. Any rule
 * added here must be added there, and vice versa.
 */
export const LANES = ["response_feedback", "bug", "incident"] as const
export type Lane = (typeof LANES)[number]

export const SENTIMENTS = ["up", "down"] as const
export type Sentiment = (typeof SENTIMENTS)[number]

export const SEVERITIES = ["cosmetic", "annoying", "blocks_workflow", "work_stopped"] as const
export type Severity = (typeof SEVERITIES)[number]

export const REPRODUCIBILITIES = ["once", "sometimes", "always", "not_sure"] as const
export type Reproducibility = (typeof REPRODUCIBILITIES)[number]

export const WHO_AFFECTED = ["only_me", "team", "multiple_teams", "other", "unknown"] as const
export type WhoAffected = (typeof WHO_AFFECTED)[number]

// -----------------------------------------------------------------------------
// Per-lane zod schemas
// -----------------------------------------------------------------------------

export const responseFeedbackSchema = z.object({
  sentiment: z.enum(SENTIMENTS),
  comment: z.string().max(2000).optional().or(z.literal("")),
})
export type ResponseFeedbackFields = z.infer<typeof responseFeedbackSchema>

export const bugReportSchema = z.object({
  whatTrying: z.string().trim().min(1, "Required").max(2000),
  expected: z.string().trim().min(1, "Required").max(2000),
  happened: z.string().trim().min(1, "Required").max(2000),
  errorText: z.string().max(4000).optional().or(z.literal("")),
  reproducibility: z.enum(REPRODUCIBILITIES),
  whoAffected: z.enum(WHO_AFFECTED),
  whoAffectedOther: z.string().max(500).optional().or(z.literal("")),
  severity: z.enum(SEVERITIES),
  whenStarted: z.string().max(64).optional().or(z.literal("")),
  pageFeature: z.string().max(500).optional().or(z.literal("")),
  /** Manually-uploaded evidence image (user-picked). */
  evidenceBlobPath: z.string().optional().or(z.literal("")),
  /** Auto-captured screenshot of the app at dialog-open time. */
  screenshotBlobPath: z.string().optional().or(z.literal("")),
})
export type BugReportFields = z.infer<typeof bugReportSchema>

export const incidentSchema = z.object({
  summary: z.string().trim().min(1, "Required").max(2000),
  impact: z.string().trim().min(1, "Required").max(4000),
  timeframe: z.string().max(500).optional().or(z.literal("")),
  errorText: z.string().max(4000).optional().or(z.literal("")),
  evidenceBlobPath: z.string().optional().or(z.literal("")),
  screenshotBlobPath: z.string().optional().or(z.literal("")),
})
export type IncidentFields = z.infer<typeof incidentSchema>

export type LaneFields =
  | { lane: "response_feedback"; fields: ResponseFeedbackFields }
  | { lane: "bug"; fields: BugReportFields }
  | { lane: "incident"; fields: IncidentFields }

// -----------------------------------------------------------------------------
// Escalation rule — mirrors the Python `should_escalate` function.
// -----------------------------------------------------------------------------

export type EscalationResult = {
  escalate: boolean
  reasons: string[]
}

export function shouldEscalate(
  lane: Lane,
  fields: Partial<BugReportFields> | object
): EscalationResult {
  if (lane === "incident") {
    return { escalate: true, reasons: ["lane=incident"] }
  }
  if (lane === "response_feedback") {
    return { escalate: false, reasons: [] }
  }

  const bugFields = fields as Partial<BugReportFields>
  const reasons: string[] = []
  if (bugFields.severity === "work_stopped") {
    reasons.push("severity=work_stopped")
  }
  if (bugFields.severity === "blocks_workflow") {
    reasons.push("severity=blocks_workflow")
  }
  if (bugFields.whoAffected === "multiple_teams") {
    reasons.push("whoAffected=multiple_teams")
  }
  const errorText = (bugFields.errorText ?? "").trim()
  if (bugFields.reproducibility === "always" && errorText.length > 0) {
    reasons.push("always-reproducible-with-error")
  }
  return { escalate: reasons.length > 0, reasons }
}
